import React, { useState, useEffect, useCallback } from "react";
import Seo from '../components/Seo';
import { Link, useNavigate } from "react-router-dom";
import "../styles/portfolio.css";
import API from "../utils/api";
import { resolveAssetUrl } from "../utils/config";

const DEFAULT_THUMB = "/images/default-portfolio.png";

// images: [{ id, url }] (또는 문자열) → 대표 썸네일 URL
const getThumbUrl = (images) => {
  const first = Array.isArray(images) ? images[0] : images;
  const path = first && typeof first === "object" ? first.url : first;
  return path ? resolveAssetUrl(path) : DEFAULT_THUMB;
};

const Portfolio = () => {
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem("accessToken"));
  }, []);

  const fetchPortfolios = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await API.get("/portfolios", { params: { page, size } });
      const data = res.data;
      const content = data?.content;

      if (content?.list) {
        setPortfolioItems(content.list);
        setTotalPages(content.totalPage || 1);
      } else if (Array.isArray(content)) {
        setPortfolioItems(content);
        setTotalPages(data.totalPage || 1);
      } else if (Array.isArray(data)) {
        setPortfolioItems(data);
        setTotalPages(1);
      } else {
        setPortfolioItems([]);
        setTotalPages(0);
      }
    } catch (err) {
      setError("포트폴리오 목록을 불러오는데 실패했습니다. 네트워크 연결을 확인해주세요.");
      setPortfolioItems([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) setPage(newPage);
  };

  const handleSizeChange = (e) => {
    setSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <div className="portfolio-list container section">
      <Seo
        title="포트폴리오"
        description="이지랩이 직접 기획·개발한 웹과 앱 프로젝트를 소개합니다. 실제 서비스로 운영 중인 결과물을 확인해 보세요."
        path="/portfolio"
      />
      <header className="portfolio-header">
        <div>
          <span className="section__eyebrow">Portfolio</span>
          <h1 className="portfolio-header__title">포트폴리오</h1>
        </div>
        {isLoggedIn && (
          <div className="portfolio-actions">
            <button className="btn btn--primary btn--sm" onClick={() => navigate("/portfolio/write")}>
              포트폴리오 작성
            </button>
          </div>
        )}
      </header>

      {loading ? (
        <div className="state-block">
          <div className="spinner" />
          포트폴리오를 불러오는 중...
        </div>
      ) : error ? (
        <div className="state-block">
          <p className="state-block__title">불러오지 못했습니다</p>
          <p>{error}</p>
          <button onClick={fetchPortfolios} className="btn btn--outline btn--sm" style={{ marginTop: "1rem" }}>
            다시 시도
          </button>
        </div>
      ) : portfolioItems.length > 0 ? (
        <>
          <div className="portfolio-grid">
            {portfolioItems.map((item) => (
              <Link
                to={`/portfolio/${item.id}`}
                key={item.id}
                className="portfolio-card card card--interactive"
              >
                <div className="portfolio-card__image">
                  <img
                    src={getThumbUrl(item.images)}
                    alt={item.title || "포트폴리오 썸네일"}
                    loading="lazy"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_THUMB;
                    }}
                  />
                </div>
                <div className="portfolio-card__body">
                  <h3 className="portfolio-card__title">{item.title}</h3>
                  {item.type && <span className="badge">{item.type}</span>}
                </div>
              </Link>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="btn btn--outline btn--sm"
              >
                이전
              </button>
              <span className="pagination__info">{page} / {totalPages}</span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages}
                className="btn btn--outline btn--sm"
              >
                다음
              </button>
              <select value={size} onChange={handleSizeChange} className="select pagination__size">
                <option value="5">5개씩</option>
                <option value="10">10개씩</option>
                <option value="20">20개씩</option>
              </select>
            </div>
          )}
        </>
      ) : (
        <div className="state-block">
          <p className="state-block__title">등록된 포트폴리오가 없습니다</p>
          {isLoggedIn && (
            <button
              onClick={() => navigate("/portfolio/write")}
              className="btn btn--primary btn--sm"
              style={{ marginTop: "1rem" }}
            >
              첫 포트폴리오 작성하기
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Portfolio;
